/**
 * ResendVerificationPage Component
 * Allows users with incomplete signups to resend their verification code.
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { PageLayout } from '@/components';
import { useAuthContext } from '@/contexts/AuthContext';
import { Button, Input, Card, IconContainer } from '@/components/ui';

export const ResendVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { resendVerificationCode } = useAuthContext();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await resendVerificationCode(email);
      setSuccess(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('forgotPassword.failedResendCode');
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-12 sm:pt-24">
          <div className="max-w-md w-full">
            <Card variant="form" padding="xl" className="text-center">
              <IconContainer variant="success" size="xl" className="mx-auto mb-4">
                <CheckCircle className="w-8 h-8" />
              </IconContainer>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                {t('signup.codeSent')}
              </h1>
              <p className="text-slate-600 mb-6">
                {t('signup.codeSentMessage', { email })}
              </p>
              <Button asChild variant="success" size="lg" fullWidth>
                <Link
                  to="/signup"
                  state={{ email, resumeVerification: true }}
                >
                  {t('signup.continueToVerification')}
                </Link>
              </Button>
              <p className="mt-4 text-sm text-slate-500">
                {t('signup.didntReceiveCode')}{' '}
                <button
                  onClick={() => setSuccess(false)}
                  className="text-emerald-600 hover:text-emerald-700"
                >
                  {t('signup.tryAgain')}
                </button>
              </p>
            </Card>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-12 sm:pt-24">
        <div className="max-w-md w-full">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>

          <div className="text-center mb-8">
            <IconContainer variant="primary" size="xl" className="mx-auto mb-4">
              <Mail className="w-8 h-8" />
            </IconContainer>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {t('signup.resendVerificationTitle')}
            </h1>
            <p className="text-slate-600">
              {t('signup.enterEmailForVerification')}
            </p>
          </div>

          <Card variant="form" padding="xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  {t('signup.emailAddressLabel')}
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  variant="emerald"
                  inputSize="lg"
                  required
                />
                <p className="mt-2 text-sm text-slate-500">
                  {t('signup.enterSignupEmail')}
                </p>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !email}
                variant="success"
                size="lg"
                fullWidth
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('signup.sending')}
                  </>
                ) : (
                  t('signup.sendVerificationCodeButton')
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-600 text-center">
                {t('signup.rememberPassword')}{' '}
                <Link
                  to="/login"
                  className="text-emerald-600 font-medium hover:text-emerald-700"
                >
                  {t('signup.signIn')}
                </Link>
              </p>
              <p className="text-sm text-slate-600 text-center mt-2">
                {t('signup.needAccount')}{' '}
                <Link
                  to="/signup"
                  className="text-emerald-600 font-medium hover:text-emerald-700"
                >
                  {t('login.signUp')}
                </Link>
              </p>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default ResendVerificationPage;
